import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { TransferHeaderFrame, buildACHBatchBreakdownText, pickACHBatchAmounts } from '~/lib/transfers';
import { Flex, Text } from '~/styles';
import { ACHTransferBatch } from '~/typings/API';
import { formatDate, formatDateTime } from '~/util';

import { getBatchStatusDisplay } from './transferHelpers';

const Meta = styled.ul`
  display: flex;
  align-items: center;
  gap: 6px 16px;
  list-style: none;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
  }
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

const Balance = styled.div`
  display: flex;
  align-items: stretch;
  gap: 32px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

// Splits the credit and debit stats: a vertical rule on desktop that lies flat between them once they
// stack on mobile.
const BalanceDivider = styled.div`
  align-self: stretch;
  width: 1px;
  background: ${({ theme }) => theme.secondary.blendToBackground(200)};

  @media (max-width: 900px) {
    width: auto;
    height: 1px;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const SpinIcon = styled.span<{ $spin?: boolean }>`
  display: inline-flex;
  ${({ $spin }) =>
    $spin &&
    css`
      animation: ${spin} 1.2s linear infinite;
    `}
`;

const IN_PROGRESS_STATUSES = new Set(['validating', 'processing']);

const dayNumber = (iso: string, timeZoneFormat: 'local' | 'utc') =>
  formatDate(iso, { dateFormat: 'short', timeZoneFormat }).split(' ')[1].replace(',', '');

interface AchBatchHeaderProps {
  batch: ACHTransferBatch;
}

export const AchBatchHeader: React.FC<AchBatchHeaderProps> = ({ batch }) => {
  const transferLabel = batch.totalCount === 1 ? 'Transfer' : 'Transfers';
  const breakdownText = useMemo(() => buildACHBatchBreakdownText(batch), [batch]);

  const status = getBatchStatusDisplay(batch);

  return (
    <TransferHeaderFrame idLabel="Batch ID" idValue={batch.id}>
      <Flex flexDirection="column" gap={hasBothTotals(batch) ? 12 : 8}>
        {renderAmounts(batch)}

        <Meta>
          <li>
            <Chip
              variant="subtle"
              type={status.type}
              icon={<SpinIcon $spin={IN_PROGRESS_STATUSES.has(batch.status)}>{status.icon}</SpinIcon>}
              size="large"
            >
              {status.label}
            </Chip>
          </li>
          <li>
            <Chip icon={<Icon.CircleArrowUp />} size="large" variant="subtle">
              Outgoing
            </Chip>
          </li>
          <li>
            <Chip icon={<Icon.Folder />} size="large" variant="subtle">
              {breakdownText || `${batch.totalCount} ${transferLabel}`}
            </Chip>
          </li>
          <li>
            <DateRow>
              <DayDisplay>
                <DayLocal>{dayNumber(batch.createdAt, 'local')}</DayLocal>
                <DayUTC>{dayNumber(batch.createdAt, 'utc')}</DayUTC>
                <Icon.Calendar />
              </DayDisplay>
              <DateTimeDisplay>
                <DateTimeLocal>
                  <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                    {formatDateTime(batch.createdAt, { timeZoneFormat: 'local' })}
                  </Text>
                </DateTimeLocal>
                <DateTimeUTC>
                  <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                    {formatDateTime(batch.createdAt, { timeZoneFormat: 'utc' })}
                  </Text>
                </DateTimeUTC>
              </DateTimeDisplay>
            </DateRow>
          </li>
        </Meta>
      </Flex>
    </TransferHeaderFrame>
  );
};

type StatColor = 'foreground' | 'success' | 'lighter';

// Two-column grid: a leading sign (e.g. the debit's "-") sits in its own auto-width column so the
// amount and the label below it both align to the second column — i.e. the label lines up with the
// first digit of the amount, not the minus. For positive values the sign column collapses to 0.
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: baseline;
  column-gap: 0;
  row-gap: 4px;
  width: fit-content;
`;

const Stat: React.FC<{
  label: string;
  value: string;
  color?: StatColor;
}> = ({ label, value, color = 'foreground' }) => {
  const negative = value.startsWith('-');
  const sign = negative ? value.slice(0, 1) : '';
  const magnitude = negative ? value.slice(1) : value;
  return (
    <StatGrid>
      {sign && (
        <Text size={24} weight="semibold" color={color} letterSpacing="tighter" style={{ gridColumn: 1, gridRow: 1 }}>
          {sign}
        </Text>
      )}
      <Flex alignItems="baseline" gap={4} style={{ gridColumn: 2, gridRow: 1 }}>
        <Text size={24} weight="semibold" color={color} letterSpacing="tighter">
          {magnitude}
        </Text>
        <Text size={14} color="lighter" weight="medium">
          USD
        </Text>
      </Flex>
      {label && (
        <Text size={14} color="light" weight="medium" whiteSpace="nowrap" style={{ gridColumn: 2, gridRow: 2 }}>
          {label}
        </Text>
      )}
    </StatGrid>
  );
};

const placeholderAmount = (
  <Balance>
    <Text size={24} weight="semibold" color="lighter" letterSpacing="tighter">
      -
    </Text>
  </Balance>
);

// True only when the batch shows two distinct totals (a non-zero credit and a non-zero debit). The
// side-by-side totals get extra vertical breathing room; a single total uses the standard spacing that
// the check-batch header and other transfer types share.
const hasBothTotals = (batch: ACHTransferBatch): boolean => {
  const picked = pickACHBatchAmounts(batch);
  return !!picked && picked.credit > 0 && picked.debit > 0;
};

const renderAmounts = (batch: ACHTransferBatch) => {
  const picked = pickACHBatchAmounts(batch);
  if (!picked) return placeholderAmount;

  const { phase, credit, debit } = picked;
  const isValidated = phase === 'validated';
  const hasCredit = credit > 0;
  const hasDebit = debit > 0;
  if (!hasCredit && !hasDebit) return placeholderAmount;

  const isMixed = hasCredit && hasDebit;
  const directionLabel = (direction: 'Credit' | 'Debit') =>
    isValidated ? (isMixed ? `Validated ${direction}` : 'Validated') : isMixed ? direction : '';

  return (
    <Balance>
      {hasCredit && <Stat label={directionLabel('Credit')} value={`-${formatNumber(credit, 'USD')}`} />}
      {isMixed && <BalanceDivider />}
      {hasDebit && <Stat label={directionLabel('Debit')} value={formatNumber(debit, 'USD')} color="success" />}
    </Balance>
  );
};
